import {
  AlertCircle,
  CheckCircle,
  Clock,
  FileCheck,
  FileText,
  Loader2,
  Trash2,
  XCircle,
} from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  Label,
  Separator,
  Skeleton,
  buttonVariants,
  cn,
} from '@cold-ambulance/shadcn';
import {
  PageContainer,
  UploadField,
  uploadFile,
  useAction,
  useData,
} from '@cold-ambulance/ui';

export default function OfferDocumentsPage() {
  const { t } = useTranslation();
  const { offerId } = useParams();
  const [uploadingRequirementId, setUploadingRequirementId] = useState<
    string | null
  >(null);
  const [deletingDocId, setDeletingDocId] = useState<string | null>(null);

  const documentsQuery = useData(
    'GET /offers/{id}/documents',
    { id: offerId! },
    { enabled: !!offerId },
  );

  const uploadDocument = useAction('POST /offers/{id}/documents', {
    invalidate: ['GET /offers/{id}/documents'],
    meta: { toast: true },
    onSuccess: () => toast.success(t('shared.actions.offer.documentUploaded')),
  });

  const deleteDocument = useAction('DELETE /offers/{id}/documents/{docId}', {
    invalidate: ['GET /offers/{id}/documents'],
    meta: { toast: true },
    onSuccess: () => toast.success(t('shared.actions.offer.documentDeleted')),
  });

  async function handleFileChange(
    requirementId: string,
    file: File | null,
    existingDocId?: string,
  ) {
    if (!offerId) return;

    if (!file && existingDocId) {
      setDeletingDocId(existingDocId);
      try {
        await deleteDocument.mutateAsync({
          id: offerId,
          docId: existingDocId,
        });
      } catch (error) {
        console.error('Error deleting document:', error);
      } finally {
        setDeletingDocId(null);
      }
      return;
    }

    if (!file) return;

    setUploadingRequirementId(requirementId);
    try {
      const fileId = await uploadFile(file);
      await uploadDocument.mutateAsync({
        id: offerId,
        requirementId,
        fileId,
      });
    } catch (error) {
      console.error('Error uploading document:', error);
    } finally {
      setUploadingRequirementId(null);
    }
  }

  if (documentsQuery.isLoading) {
    return (
      <div className="page-container">
        <div className="mx-auto w-full max-w-2xl">
          <DocumentsPageSkeleton />
        </div>
      </div>
    );
  }

  if (documentsQuery.isError) {
    const errorMessage =
      documentsQuery.error instanceof Error
        ? documentsQuery.error.message
        : t('documents.errorLoading');

    return (
      <div className="page-container">
        <div className="mx-auto w-full max-w-2xl">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{t('common.error')}</AlertTitle>
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
          <Link
            to="/offers"
            className={buttonVariants({
              variant: 'outline',
              className: 'mt-4',
            })}
          >
            {t('offers.backToOffers')}
          </Link>
        </div>
      </div>
    );
  }

  const data = documentsQuery.data;
  if (!data) return null;

  const allUploaded = data.documents.every((doc) => doc.uploaded);
  const uploadedCount = data.documents.filter((doc) => doc.uploaded).length;
  const totalCount = data.documents.length;

  return (
    <PageContainer title={t('documents.uploadRequired')} showBackButton>
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">
              {t('documents.uploadedCount', { uploaded: uploadedCount, total: totalCount })}
            </span>
            {allUploaded ? (
              <div className="flex items-center gap-2 text-green-600">
                <FileCheck className="h-4 w-4" />
                <span className="text-sm font-medium">{t('documents.uploadComplete')}</span>
              </div>
            ) : (
              <span className="text-muted-foreground text-sm">
                {t('documents.remaining', { count: totalCount - uploadedCount })}
              </span>
            )}
          </div>
          <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-gray-200">
            <div
              className={cn(
                'h-full transition-all',
                allUploaded ? 'bg-green-500' : 'bg-primary',
              )}
              style={{ width: `${(uploadedCount / totalCount) * 100}%` }}
            />
          </div>
        </CardContent>
      </Card>

      <div className="space-y-6">
        {data.documents.map((doc) =>
          doc.uploaded &&
          doc.document &&
          doc.document.status !== 'NOT_SUBMITTED' ? (
            <UploadedDocument
              key={doc.requirementId}
              label={doc.label}
              status={doc.document.status}
              rejectionReason={doc.document.rejectionReason}
              isDeleting={deletingDocId === doc.document.id}
              onDelete={() =>
                handleFileChange(doc.requirementId, null, doc.document?.id)
              }
            />
          ) : (
            <UploadField
              key={doc.requirementId}
              label={doc.label}
              name={doc.requirementId}
              onChange={(file) => handleFileChange(doc.requirementId, file)}
              suffix={
                uploadingRequirementId === doc.requirementId ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : null
              }
            />
          ),
        )}
      </div>

      <Separator className="my-8" />

      {allUploaded && (
        <Card className="mb-6 border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <FileCheck className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" />
              <div>
                <p className="font-medium text-blue-800">
                  {t('documents.allUploaded')}
                </p>
                <p className="mt-1 text-sm text-blue-700">
                  {t('documents.allUploadedMessage')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </PageContainer>
  );
}

function UploadedDocument({
  label,
  status,
  rejectionReason,
  isDeleting,
  onDelete,
}: {
  label: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  rejectionReason?: string | null;
  isDeleting: boolean;
  onDelete: () => void;
}) {
  const { t } = useTranslation();

  const statusConfig = {
    APPROVED: {
      bgColor: 'bg-green-50',
      textColor: 'text-green-800',
      iconColor: 'text-green-600',
      icon: CheckCircle,
      label: t('documents.approved'),
      badgeBg: 'bg-green-100',
      badgeText: 'text-green-700',
    },
    PENDING: {
      bgColor: 'bg-amber-50',
      textColor: 'text-amber-800',
      iconColor: 'text-amber-600',
      icon: Clock,
      label: t('documents.underReview'),
      badgeBg: 'bg-amber-100',
      badgeText: 'text-amber-700',
    },
    REJECTED: {
      bgColor: 'bg-red-50',
      textColor: 'text-red-800',
      iconColor: 'text-red-600',
      icon: XCircle,
      label: t('documents.rejected'),
      badgeBg: 'bg-red-100',
      badgeText: 'text-red-700',
    },
  };

  const config = statusConfig[status];
  const StatusIcon = config.icon;

  return (
    <div className="space-y-2">
      <Label className="flex items-center gap-2 text-sm font-medium">
        {label}
        <span
          className={cn(
            'flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
            config.badgeBg,
            config.badgeText,
          )}
        >
          <StatusIcon className="h-3 w-3" />
          {config.label}
        </span>
      </Label>

      {status === 'REJECTED' && rejectionReason && (
        <Alert variant="destructive" className="py-2">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle className="text-sm">{t('documents.rejectionReason')}</AlertTitle>
          <AlertDescription className="text-sm">
            {rejectionReason}
          </AlertDescription>
        </Alert>
      )}

      <div
        className={cn(
          'flex h-12 w-full items-center justify-between rounded-md border-none px-3 py-2 text-sm',
          config.bgColor,
        )}
      >
        <div className="flex items-center gap-2">
          <FileText size={16} className={config.iconColor} />
          <span className={config.textColor}>{t('documents.documentUploaded')}</span>
        </div>
        {status !== 'APPROVED' && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onDelete}
            disabled={isDeleting}
            className="text-destructive hover:text-destructive/90 h-6 shrink-0 cursor-pointer px-2"
          >
            {isDeleting ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Trash2 size={14} />
            )}
          </Button>
        )}
      </div>
    </div>
  );
}

function DocumentsPageSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-8 w-32" />
      <Skeleton className="h-10 w-64" />
      <Skeleton className="h-5 w-48" />

      <div className="space-y-4 rounded-lg border p-4">
        <div className="flex items-center justify-between">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-4 w-20" />
        </div>
        <Skeleton className="h-2 w-full" />
      </div>

      <div className="space-y-6">
        {[1, 2, 3].map((i) => (
          <div key={i} className="space-y-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-12 w-full rounded-md" />
          </div>
        ))}
      </div>
    </div>
  );
}
